import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, Sparkles, BookOpen, Brain, FileText, ClipboardList, Lock, Loader2, Languages, Share2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import ShareToGroupModal from "@/components/ShareToGroupModal";

const KINDS = [
  { kind: "notes", label: "Notes", icon: BookOpen },
  { kind: "flashcards", label: "Flashcards", icon: Brain },
  { kind: "practice_test", label: "Practice Test", icon: ClipboardList },
  { kind: "study_guide", label: "Study Guide", icon: FileText },
  { kind: "worksheet", label: "Worksheet (Pro)", icon: FileText },
  { kind: "study_plan", label: "Study Plan", icon: Sparkles },
];

const RecordingDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [rec, setRec] = useState(null);
  const [busy, setBusy] = useState(null);
  const [privateOn, setPrivateOn] = useState(false);
  const [showEn, setShowEn] = useState(true);
  const [shareItem, setShareItem] = useState(null);

  const load = async () => {
    const { data } = await api.get(`/recordings/${id}`);
    setRec(data);
  };
  useEffect(() => { load(); }, [id]);

  const generate = async (kind) => {
    setBusy(kind);
    try {
      await api.post("/study/generate", { recording_id: id, kind, private: privateOn });
      toast.success(`${kind.replace("_", " ")} generated`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(null); }
  };

  if (!rec) return <div className="p-10 text-neutral-500 text-[0.86rem]">Loading…</div>;

  const hasTranslation = !!rec.transcript_en;

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto">
      <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-[0.82rem] text-neutral-500 hover:text-white transition-colors mb-8" data-testid="back-dashboard">
        <ChevronLeft size={13}/> back to dashboard
      </Link>
      <header className="mb-10 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-mono mb-3">/ {rec.subject.toLowerCase()}</div>
          <h1 className="font-display text-3xl md:text-4xl tightest font-semibold leading-tight">Lesson · {new Date(rec.created_at).toLocaleString()}</h1>
        </div>
        {hasTranslation && (
          <button
            onClick={() => setShowEn(!showEn)}
            className="inline-flex items-center gap-2 text-[0.78rem] px-3 py-1.5 border border-white/[0.08] hover:border-white/25 rounded-[2px] transition-colors"
            data-testid="toggle-en"
          >
            <Languages size={13} className="text-amber-400" />
            {showEn ? "Hide English" : "Show English"}
          </button>
        )}
      </header>

      <div className="grid md:grid-cols-3 gap-4 mb-10">
        <div className="md:col-span-2 glass p-6">
          <div className="label-mono mb-4 flex items-center gap-2">
            <span>/ transcript</span>
            {rec.language && rec.language !== "auto" && rec.language !== "en" && (
              <span className="ml-auto text-[0.68rem] font-mono px-1.5 py-0.5 border border-amber-500/30 text-amber-400 bg-amber-500/[0.06] rounded-[2px] uppercase tracking-[0.16em]">
                {rec.language}
              </span>
            )}
          </div>
          <div className="text-neutral-200 text-[0.88rem] whitespace-pre-wrap leading-relaxed max-h-[26rem] overflow-y-auto pr-2 i18n-text" data-testid="transcript-text">
            {rec.transcript || "(empty)"}
          </div>
          {hasTranslation && showEn && (
            <div className="mt-5 pt-5 border-t border-white/[0.06]" data-testid="transcript-en-block">
              <div className="label-mono mb-2 flex items-center gap-2">
                <Languages size={11} className="text-amber-400" />
                / english translation
              </div>
              <div className="text-neutral-400 text-[0.86rem] whitespace-pre-wrap leading-relaxed max-h-[22rem] overflow-y-auto pr-2" data-testid="transcript-en-text">
                {rec.transcript_en}
              </div>
            </div>
          )}
        </div>
        <div className="glass p-6">
          <div className="label-mono mb-4">/ generate</div>
          <label className="flex items-center gap-2 text-[0.78rem] mb-4 cursor-pointer text-neutral-400" data-testid="private-toggle">
            <input type="checkbox" checked={privateOn} onChange={(e) => setPrivateOn(e.target.checked)} disabled={user?.plan === "free"} />
            <Lock size={11}/> Private (Starter/Pro)
          </label>
          <div className="grid grid-cols-2 gap-2">
            {KINDS.map(({ kind, label, icon: Icon }) => (
              <button
                key={kind}
                onClick={() => generate(kind)}
                disabled={!!busy}
                data-testid={`gen-${kind}`}
                className="flex flex-col items-start gap-2 p-3 border border-white/[0.08] rounded-[2px] hover:border-white/25 hover:bg-white/[0.03] transition-colors text-left disabled:opacity-40"
              >
                {busy === kind ? (
                  <Loader2 size={14} className="animate-spin text-white"/>
                ) : (
                  <Icon size={14} className="text-neutral-300"/>
                )}
                <span className="text-[0.75rem] text-neutral-200">{label}</span>
              </button>
            ))}
          </div>
          {hasTranslation && (
            <p className="text-[0.7rem] text-neutral-500 mt-4 leading-relaxed">
              This lesson has both the original transcript and an English translation. All generated flashcards, tests, notes and guides will include both languages.
            </p>
          )}
        </div>
      </div>

      <section className="space-y-4">
        {rec.study_items?.map((it) => (
          <StudyItemView
            key={it.id}
            item={{ ...it, subject: rec.subject }}
            showEn={showEn}
            onShare={() => setShareItem({ ...it, subject: rec.subject })}
          />
        ))}
      </section>

      <ShareToGroupModal
        open={!!shareItem}
        onClose={() => setShareItem(null)}
        item={shareItem}
      />
    </div>
  );
};

const ShareButton = ({ onClick, disabled }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    data-testid="study-item-share"
    className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[0.72rem] border border-white/[0.08] hover:border-amber-500/40 hover:bg-amber-500/[0.06] hover:text-amber-300 rounded-[2px] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
    title={disabled ? "Private items can't be shared" : "Share to a study group"}
  >
    <Share2 size={11}/> Share
  </button>
);

const StudyItemView = ({ item, showEn, onShare }) => {
  if (item.kind === "flashcards" && item.data?.cards) return <Flashcards item={item} showEn={showEn} onShare={onShare} />;
  if (item.kind === "practice_test" && item.data?.questions) return <PracticeTest item={item} showEn={showEn} onShare={onShare} />;
  return (
    <div className="glass p-6" data-testid={`item-${item.id}`}>
      <div className="label-mono mb-4 flex items-center justify-between">
        <span>{item.kind.replace("_", " ")}</span>
        <div className="flex items-center gap-2">
          {item.private && <span className="text-rose-400 text-[9px]">PRIVATE</span>}
          <ShareButton onClick={onShare} disabled={item.private} />
        </div>
      </div>
      <div
        className="markdown-out i18n-text text-[0.9rem]"
        dangerouslySetInnerHTML={{ __html: renderMarkdown(item.content, showEn) }}
      />
    </div>
  );
};

const Flashcards = ({ item, showEn, onShare }) => {
  const cards = item.data.cards || [];
  const [i, setI] = useState(0);
  const [flipped, setFlipped] = useState(false);
  if (!cards.length) return null;
  const card = cards[i];
  return (
    <div className="glass p-6" data-testid={`flashcards-${item.id}`}>
      <div className="label-mono mb-4 flex items-center justify-between">
        <span>flashcards · {i + 1} / {cards.length}</span>
        <div className="flex items-center gap-2">
          {item.private && <span className="text-rose-400 text-[9px]">PRIVATE</span>}
          <ShareButton onClick={onShare} disabled={item.private} />
        </div>
      </div>
      <div className={`flip-card h-64 cursor-pointer mb-4 ${flipped ? "flipped" : ""}`} onClick={() => setFlipped(!flipped)} data-testid="flashcard">
        <div className="flip-inner">
          <div className="flip-face glass-strong text-center">
            <div className="flex flex-col items-center gap-3 px-4">
              <div className="font-display text-2xl tightest font-semibold i18n-text">{card.front}</div>
              {showEn && card.front_en && card.front_en !== card.front && (
                <div className="text-neutral-500 text-[0.86rem] leading-snug max-w-md" data-testid="flashcard-front-en">
                  {card.front_en}
                </div>
              )}
            </div>
          </div>
          <div className="flip-face flip-back glass-strong text-center">
            <div className="flex flex-col items-center gap-3 px-4">
              <div className="text-amber-400 text-lg i18n-text font-medium">{card.back}</div>
              {showEn && card.back_en && card.back_en !== card.back && (
                <div className="text-neutral-500 text-[0.86rem] leading-snug max-w-md" data-testid="flashcard-back-en">
                  {card.back_en}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="flex gap-2">
        <button className="btn-secondary flex-1" onClick={() => { setFlipped(false); setI((i - 1 + cards.length) % cards.length); }} data-testid="card-prev">Prev</button>
        <button className="btn-primary flex-1" onClick={() => { setFlipped(false); setI((i + 1) % cards.length); }} data-testid="card-next">Next</button>
      </div>
    </div>
  );
};

const PracticeTest = ({ item, showEn, onShare }) => {
  const qs = item.data.questions || [];
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const score = submitted ? qs.filter((q, i) => answers[i] === q.answer).length : 0;

  const submit = async () => {
    setSubmitted(true);
    const correct = qs.filter((q, i) => answers[i] === q.answer).length;
    try {
      await api.post("/tests/attempt", {
        study_item_id: item.id,
        score: correct,
        total: qs.length,
        wrong_questions: qs.filter((q, i) => answers[i] !== q.answer).map((q) => q.q?.slice(0, 120)),
      });
      toast.success(`Scored ${correct}/${qs.length} · added to your predicted grade`);
    } catch { /* score still shown locally */ }
  };
  return (
    <div className="glass p-6" data-testid={`test-${item.id}`}>
      <div className="label-mono mb-4 flex items-center justify-between">
        <span>practice test</span>
        <div className="flex items-center gap-2">
          {submitted && <span className="text-amber-500 text-[0.72rem] tabular">Score {score}/{qs.length}</span>}
          {item.private && <span className="text-rose-400 text-[9px]">PRIVATE</span>}
          <ShareButton onClick={onShare} disabled={item.private} />
        </div>
      </div>
      <div className="space-y-6">
        {qs.map((q, qi) => (
          <div key={qi} className="border-l border-white/[0.08] pl-4">
            <div className="mb-3">
              <div className="font-medium text-[0.9rem] i18n-text">{qi + 1}. {q.q}</div>
              {showEn && q.q_en && q.q_en !== q.q && (
                <div className="text-[0.78rem] text-neutral-500 mt-1 leading-snug" data-testid={`q${qi}-en`}>
                  <span className="font-mono text-neutral-600 mr-1">EN</span>{q.q_en}
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              {q.choices.map((c, ci) => {
                const selected = answers[qi] === ci;
                const correct = submitted && ci === q.answer;
                const wrong = submitted && selected && ci !== q.answer;
                const cen = Array.isArray(q.choices_en) ? q.choices_en[ci] : null;
                return (
                  <button
                    key={ci}
                    onClick={() => !submitted && setAnswers({ ...answers, [qi]: ci })}
                    data-testid={`q${qi}-c${ci}`}
                    className={`block w-full text-left px-3 py-2 border rounded-[2px] text-[0.85rem] transition-colors
                      ${correct ? "border-amber-500/60 bg-amber-500/10 text-amber-300" : ""}
                      ${wrong ? "border-rose-500/60 bg-rose-500/10 text-rose-200" : ""}
                      ${!submitted && selected ? "border-white/40 bg-white/[0.03]" : "border-white/[0.08]"}`}
                  >
                    <div className="i18n-text">{String.fromCharCode(65 + ci)}. {c}</div>
                    {showEn && cen && cen !== c && (
                      <div className="text-[0.72rem] text-neutral-500 mt-0.5 pl-4">{cen}</div>
                    )}
                  </button>
                );
              })}
            </div>
            {submitted && (q.explanation || q.explanation_en) && (
              <div className="text-[0.75rem] text-neutral-500 mt-2 leading-relaxed">
                {q.explanation && <div className="i18n-text">→ {q.explanation}</div>}
                {showEn && q.explanation_en && q.explanation_en !== q.explanation && (
                  <div className="text-neutral-600 mt-1"><span className="font-mono mr-1">EN</span>{q.explanation_en}</div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      {!submitted && <button className="btn-primary mt-6" onClick={submit} data-testid="submit-test">Submit</button>}
    </div>
  );
};

// Bilingual markdown: strips the "> EN:" translation lines when showEn is false.
function renderMarkdown(md, showEn) {
  if (!md) return "";
  let text = md;
  if (showEn === false) {
    // Remove any blockquote line beginning with "EN:" (case-insensitive) so only the source
    // language renders. Handles either "> EN: ..." or "> **EN:** ..." patterns.
    text = text
      .split(/\r?\n/)
      .filter((line) => !/^\s*>\s*(?:\*\*)?EN:?/i.test(line))
      .join("\n");
  }
  let html = text
    .replace(/^### (.*)$/gm, "<h3>$1</h3>")
    .replace(/^## (.*)$/gm, "<h2>$1</h2>")
    .replace(/^# (.*)$/gm, "<h2>$1</h2>")
    .replace(/^>\s*(?:\*\*)?EN:?\s*(.*)$/gim, '<blockquote class="md-en"><span class="md-en-tag">EN</span> $1</blockquote>')
    .replace(/^>\s*(.*)$/gm, "<blockquote>$1</blockquote>")
    .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
    .replace(/`([^`]+)`/g, "<code>$1</code>")
    .replace(/^\s*[-*] (.+)$/gm, "<li>$1</li>")
    .replace(/(<li>.*?<\/li>)(\n<li>.*?<\/li>)*/gs, (m) => `<ul>${m}</ul>`)
    .replace(/\n\n+/g, "</p><p>");
  return `<p>${html}</p>`;
}

export default RecordingDetail;
